import type { BillingSubscriptionPriceDetailsFragment } from '@graphql/fragments/billing-subscription/price.graphql';
import type { BillingSubscriptionProductDetailsFragment } from '@graphql/fragments/billing-subscription/product.graphql';
import { useMemo } from 'react';

import { usePrices } from '../use-prices';
import { PAID_PLAN_ORDER, type PaidPlan } from './individual-products';

export interface PlanOption {
  plan: PaidPlan;
  product: BillingSubscriptionProductDetailsFragment;
  monthly?: BillingSubscriptionPriceDetailsFragment;
  annual?: BillingSubscriptionPriceDetailsFragment;
}

interface UsePlanOptions {
  /** The paid plans this environment can actually sell, cheapest first. */
  plans: PlanOption[];
  /**
   * What annual billing saves against paying monthly, in whole percent. The
   * smallest saving across the plans on offer, so a single badge can't overstate
   * any one of them. `null` when no plan can be compared or any comparable plan
   * does not save money annually.
   */
  annualSavingPercent: number | null;
  currency: string;
  currencyCode: string;
  loading: boolean;
}

function savingPercent(option: PlanOption): number | null {
  if (!option.monthly || !option.annual || option.monthly.unit_amount <= 0) {
    return null;
  }

  return Math.floor((1 - option.annual.unit_amount / (option.monthly.unit_amount * 12)) * 100);
}

function saleIntervalCount(prices: BillingSubscriptionPriceDetailsFragment[]): number {
  return (
    Number(prices.some((price) => price.interval === 'month')) +
    Number(prices.some((price) => price.interval === 'year'))
  );
}

function getPlanPrices(
  prices: BillingSubscriptionPriceDetailsFragment[],
  plan: PaidPlan,
  currencyCode: string,
): BillingSubscriptionPriceDetailsFragment[] {
  const pricesByProduct = new Map<string, BillingSubscriptionPriceDetailsFragment[]>();

  for (const price of prices) {
    if (
      price.product?.type !== 'INDIVIDUAL' ||
      price.product.plan !== plan ||
      price.currency.toLowerCase() !== currencyCode.toLowerCase()
    ) {
      continue;
    }

    const productId = price.product.provider_product_id;

    if (!productId) {
      continue;
    }

    const productPrices = pricesByProduct.get(productId) ?? [];
    productPrices.push(price);
    pricesByProduct.set(productId, productPrices);
  }

  // A complete product is preferred to incomplete fixture products in the
  // shared catalog. This still degrades to a single interval when that is all a
  // real plan currently offers.
  return (
    [...pricesByProduct.values()].sort(
      (left, right) => saleIntervalCount(right) - saleIntervalCount(left),
    )[0] ?? []
  );
}

/**
 * The individual plans available to buy, each with its monthly and annual price.
 *
 * Resolves individual products by plan, so replacing a Stripe product does not
 * require a deploy. A plan with no price is omitted, so the screen renders what
 * it can sell instead of advertising a checkout that would fail.
 */
export function usePlanOptions(): UsePlanOptions {
  // The one surface that sells Pro, so the one that asks for it.
  const { prices, currency, currencyCode, loading } = usePrices({ plans: PAID_PLAN_ORDER });

  const plans = useMemo(() => {
    return PAID_PLAN_ORDER.reduce<PlanOption[]>((options, plan) => {
      const planPrices = getPlanPrices(prices, plan, currencyCode);
      const product = planPrices[0]?.product;

      if (!product) {
        return options;
      }

      options.push({
        plan,
        product,
        monthly: planPrices.find((price) => price.interval === 'month'),
        annual: planPrices.find((price) => price.interval === 'year'),
      });

      return options;
    }, []);
  }, [prices, currencyCode]);

  const annualSavingPercent = useMemo(() => {
    const savings = plans.map(savingPercent).filter((saving): saving is number => saving !== null);
    const minimumSaving = savings.length ? Math.min(...savings) : null;

    return minimumSaving !== null && minimumSaving > 0 ? minimumSaving : null;
  }, [plans]);

  return { plans, annualSavingPercent, currency, currencyCode, loading };
}
